import React from "react";

const RefreshmentsBarGuidelines = () => {
  // Update URL hash without scrolling/jumping the page
  const handleHashClick = (e) => {
    if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return; // let users open in new tab, etc.
    e.preventDefault();
    const hash = e.currentTarget.getAttribute("href");
    if (!hash) return;
    window.history.pushState({}, "", hash); // or replaceState({}, '', hash)
  };

  return (
    <div id="refreshments-bar-guidelines" className="guidelines">
      {/* Styles specific to clickable guideline items (scoped to .guidelines) */}
      <style>{`
        /* Make ordered lists use custom numbers and spacing */
        .guidelines ol.clickable-rules {
          counter-reset: item;
          list-style: none;
          padding-left: 0;
          margin-left: 0;
        }
        .guidelines ol.clickable-rules > li {
          counter-increment: item;
          position: relative;
          padding-left: 2.25rem;         /* room for the number */
          margin: 12px 0 !important;     /* override any global li spacing */
          line-height: 1.6;
        }

        /* Number link: subtle, looks like text but clickable */
        .guidelines a.rule-num {
          position: absolute;
          left: 0;
          top: 0;
          width: 2rem;                   /* larger hit target */
          text-align: right;
          text-decoration: none;
          color: inherit;                /* no blue */
          opacity: .85;
          cursor: pointer;
        }
        .guidelines a.rule-num::before {
          content: counter(item) ".";
          font-weight: 600;
        }
        .guidelines a.rule-num:hover,
        .guidelines a.rule-num:active { text-decoration: none; opacity: 1; }
        .guidelines a.rule-num:focus { text-decoration: none; }
        .guidelines a.rule-num:focus-visible {
          outline: 2px solid currentColor;
          outline-offset: 2px;
        }

        /* Title link: subtle self-link (no blue/underline) */
        .guidelines a.rule-link {
          text-decoration: none;
          color: inherit;                /* keep same color as text */
          transition: opacity .15s ease-in-out;
        }
        .guidelines a.rule-link:visited { color: inherit; }
        .guidelines a.rule-link:hover,
        .guidelines a.rule-link:active { text-decoration: none; opacity: .9; }
        .guidelines a.rule-link:focus { text-decoration: none; }
        .guidelines a.rule-link:focus-visible {
          outline: 2px solid currentColor;  /* accessible but subtle */
          outline-offset: 2px;
        }
      `}</style>

      <h1>Refreshments Bar Posting & Commenting Guidelines</h1>

      <p>
        These guidelines are here to support thoughtful, inclusive, and on-topic conversation. All posts and comments are subject to review by moderators to ensure they align with our community’s values, especially around safety, clarity, and care.
      </p>

      <p><i>Please note: The Refreshments Bar is intended as a community-wide discussion space for focused, constructive conversation that supports Covid-conscious connection across a broad range of members. Because of that, some of the more sensitive or reactive topics that matter deeply in this community may not always be the best fit for this feature, especially when they are likely to lead to conflict or require significant moderation. We are still early in developing the community side of the app and are actively working to expand it to better support the kinds of discussions that need a different feature in order to be hosted constructively. You are welcome to submit a post even if you are unsure whether it will be a fit, but please note that we are not able to approve all posts.</i></p>

      <h2>Posting and Commenting Rules</h2>
      <ol className="clickable-rules">
        <li id="rule-1">
          <a href="#rule-1" className="rule-num" aria-label="Link to rule 1" onClick={handleHashClick} />
          <strong><a href="#rule-1" className="rule-link" aria-label="Link to rule 1 title" onClick={handleHashClick}>All posts require moderator approval</a></strong><br />
          We aim to respond to submissions within 3 business days.
        </li>
        <li id="rule-2">
          <a href="#rule-2" className="rule-num" aria-label="Link to rule 2" onClick={handleHashClick} />
          <strong><a href="#rule-2" className="rule-link" aria-label="Link to rule 2 title" onClick={handleHashClick}>Posts must follow community standards</a></strong><br />
          This includes our <a href="/terms">Terms of Service</a>, <a href="/language">Language Guidelines</a>, and <a href="guidelines/science">STEAM Communication Guidelines</a>.
        </li>
        <li id="rule-3">
          <a href="#rule-3" className="rule-num" aria-label="Link to rule 3" onClick={handleHashClick} />
          <strong><a href="#rule-3" className="rule-link" aria-label="Link to rule 3 title" onClick={handleHashClick}>Don’t repeat the same post or topic</a></strong><br />
          - Repetitive submissions may be rejected or redirected.
          - Avoid repeatedly steering discussions across posts or comments back to the same topic in ways that limit broader community discussion.
        </li>
        <li id="rule-4">
          <a href="#rule-4" className="rule-num" aria-label="Link to rule 4" onClick={handleHashClick} />
          <strong><a href="#rule-4" className="rule-link" aria-label="Link to rule 4 title" onClick={handleHashClick}>Use clear, respectful language</a></strong><br />
          Please avoid leet speak, excessive abbreviations, unnecessary profanity, or unclear colloquialisms. Standard grammar and spelling make your ideas easier for the whole community to engage with.
        </li>
        <li id="rule-5">
          <a href="#rule-5" className="rule-num" aria-label="Link to rule 5" onClick={handleHashClick} />
          <strong><a href="#rule-5" className="rule-link" aria-label="Link to rule 5 title" onClick={handleHashClick}>Use English, translations welcome </a></strong><br />
          A primary language helps ensure clarity and foster respect for dialogue across an internationally diverse community. If you’re more comfortable writing in another language, you may include a translation alongside your English post.
        </li>
        <li id="rule-6">
          <a href="#rule-6" className="rule-num" aria-label="Link to rule 6" onClick={handleHashClick} />
          <strong><a href="#rule-6" className="rule-link" aria-label="Link to rule 6 title" onClick={handleHashClick}>Share original content</a></strong><br />
          All submissions should be your own writing and ideas.
        </li>
        <li id="rule-7">
          <a href="#rule-7" className="rule-num" aria-label="Link to rule 7" onClick={handleHashClick} />
          <strong><a href="#rule-7" className="rule-link" aria-label="Link to rule 7 title" onClick={handleHashClick}>Images must meet approval standards</a></strong><br />
          You must have the rights to use any images. Image-based posts are subject to the same approval process as text posts.
        </li>
        <li id="rule-8">
          <a href="#rule-8" className="rule-num" aria-label="Link to rule 8" onClick={handleHashClick} />
          <strong><a href="#rule-8" className="rule-link" aria-label="Link to rule 8 title" onClick={handleHashClick}>Protect privacy</a></strong><br />
          Do not include personal information like your phone number, email, or address in posts or comments.
        </li>
        <li id="rule-9">
          <a href="#rule-9" className="rule-num" aria-label="Link to rule 9" onClick={handleHashClick} />
          <strong><a href="#rule-9" className="rule-link" aria-label="Link to rule 9 title" onClick={handleHashClick}>No self-promotion or advertising</a></strong><br />
          This includes promoting your business, brand, nonprofit, or services.
        </li>
        <li id="rule-10">
          <a href="#rule-10" className="rule-num" aria-label="Link to rule 10" onClick={handleHashClick} />
          <strong><a href="#rule-10" className="rule-link" aria-label="Link to rule 10 title" onClick={handleHashClick}>No technical issues, bug reports, or moderation topics here</a></strong><br />
          - Use the app’s Help section or email <a href="mailto:help@refreshconnections.com">help@refreshconnections.com</a> for account support or troubleshooting.
          <br/> - Unsafe, inappropriate, bad-faith, harassing, or otherwise worrisome behavior should be reported to moderation immediately, either through the app or 
          by email at <a href="mailto:report@refreshconnections.com">report@refreshconnections.com</a>, rather than discussed in public posts. Posts centered on reporting 
          or calling out this kind of behavior are not the right fit for the public Refreshments Bar forum.
        </li>
        <li id="rule-11">
          <a href="#rule-11" className="rule-num" aria-label="Link to rule 11" onClick={handleHashClick} />
          <strong><a href="#rule-11" className="rule-link" aria-label="Link to rule 11 title" onClick={handleHashClick}>No direct connection requests in comments</a></strong><br />
          If you’d like others to connect with you based on a post, make sure your “Connect from Refreshments” setting is enabled. Use Discovery (formerly called Picks) and Personal Profiles—not the comment thread—for personal outreach.
        </li>
        <li id="rule-12">
          <a href="#rule-12" className="rule-num" aria-label="Link to rule 12" onClick={handleHashClick} />
          <strong><a href="#rule-12" className="rule-link" aria-label="Link to rule 12 title" onClick={handleHashClick}>Share links with context</a></strong><br />
          Links must be accompanied by a summary or explanation and should invite meaningful conversation. (Please remove unnecessary tracking parameters from links.)
        </li>
        <li id="rule-13">
          <a href="#rule-13" className="rule-num" aria-label="Link to rule 13" onClick={handleHashClick} />
          <strong><a href="#rule-13" className="rule-link" aria-label="Link to rule 13 title" onClick={handleHashClick}>Stay focused</a></strong><br />
          Posts should be structured around one main topic. Submit a separate post if you want to start a different conversation. If your post has over 1000 characters, it probably contains more than one topic.
        </li>
        <li id="rule-14">
          <a href="#rule-14" className="rule-num" aria-label="Link to rule 14" onClick={handleHashClick} />
          <strong><a href="#rule-14" className="rule-link" aria-label="Link to rule 14 title" onClick={handleHashClick}>No business solicitation or data collection</a></strong><br />
          Posts that aim only to collect personal data, promote services, or serve as marketing for a business are not allowed. This includes posts that use surveys, sign-ups, or events primarily to drive sales or referrals.
        </li>
        <li id="rule-15">
          <a href="#rule-15" className="rule-num" aria-label="Link to rule 15" onClick={handleHashClick} />
          <strong><a href="#rule-15" className="rule-link" aria-label="Link to rule 15 title" onClick={handleHashClick}>Event posts must align with community standards</a></strong><br />
          If you’re sharing a social event—including ones with a ticket price—it must follow our community guidelines and be approved at the moderators’ discretion. Ticketed events are allowed as long as they’re transparent, not for-profit in disguise, and aligned with our values around safety, inclusion, and trust. We are not accepting intimacy-centered events or public event postings at personal homes in the Refreshments Bar or Calendar. (See <a href="#rule-17">#17</a> for more about payments and data collection.)
        </li>
        <li id="rule-16">
          <a href="#rule-16" className="rule-num" aria-label="Link to rule 16" onClick={handleHashClick} />
          <strong><a href="#rule-16" className="rule-link" aria-label="Link to rule 16 title" onClick={handleHashClick}>Fundraisers or other efforts must center Covid Conscientious community or shared action</a></strong><br />
          Posts tied to a product, event, or broader effort may be approved, but direct personal fundraising is not allowed (e.g. no personal Venmos). Any payment link must be on an external page and clearly framed as part of the effort. This helps prevent solicitation fatigue and keeps the Refreshments Bar a welcoming space for mutual support and shared experience.
        </li>
        <li id="rule-17">
          <a href="#rule-17" className="rule-num" aria-label="Link to rule 17" onClick={handleHashClick} />
          <strong><a href="#rule-17" className="rule-link" aria-label="Link to rule 17 title" onClick={handleHashClick}>Payments and personal data</a></strong><br />
          If your post involves tickets, shipping, sign-ups, or collecting information, all payments and necessary data capture must happen on an organization-owned page that issues receipts and is built for this purpose (e.g., Eventbrite, Stripe checkout, or similar; no DIY forms or emails).
        </li>
        <li id="rule-18">
          <a href="#rule-18" className="rule-num" aria-label="Link to rule 18" onClick={handleHashClick} />
          <strong><a href="#rule-18" className="rule-link" aria-label="Link to rule 18 title" onClick={handleHashClick}>No job postings at this time</a></strong><br />
          At the moment, we are not accepting job postings in the Refreshments Bar.
        </li>
        <li id="rule-19">
          <a href="#rule-19" className="rule-num" aria-label="Link to rule 19" onClick={handleHashClick} />
          <strong><a href="#rule-19" className="rule-link" aria-label="Link to rule 19 title" onClick={handleHashClick}>Housing post expectations</a></strong><br />
          Provide enough information for people to determine whether they may be a good fit. For everyone's safety, share more specific details privately through Connect from Refreshments after connecting. <br /><br />
          <strong>Include:</strong>
          <ul>
            <li>General location (neighborhood or area)</li>
            <li>Budget or rent range</li>
            <li>Timeline or expected lease length</li>
            <li>General COVID precautions</li>
            <li>Relevant household expectations and values, needs, pets, or dealbreakers</li>
          </ul>
          <strong>Don't include:</strong>
          <ul>
            <li>Exact address, cross streets, apartment numbers, or other identifying location details</li>
            <li>Exact rent amounts</li>
            <li>Detailed schedules or routines (e.g., specific workdays, class schedules, travel plans)</li>
            <li>Sensitive personal information about yourself or others</li>
            <li>Derogatory labels or exclusionary language describing who is not welcome (please describe who you hope to live with instead)</li>
          </ul>
          <em>Please note: Due to our guidelines around getting to know people before meeting up, Refresh is intended for longer-term housing connections and does not support temporary or short-term housing posts or requests to crash.</em>
        </li>
      </ol>

      <h2>Moderation Norms</h2>
      <ol className="clickable-rules">
        <li id="mod-1">
          <a href="#mod-1" className="rule-num" aria-label="Link to moderation rule 1" onClick={handleHashClick} />
          <strong><a href="#mod-1" className="rule-link" aria-label="Link to moderation rule 1 title" onClick={handleHashClick}>Review time</a></strong><br />
          We respond to most submissions within 3 business days.
        </li>
        <li id="mod-2">
          <a href="#mod-2" className="rule-num" aria-label="Link to moderation rule 2" onClick={handleHashClick} />
          <strong><a href="#mod-2" className="rule-link" aria-label="Link to moderation rule 2 title" onClick={handleHashClick}>Quick approvals don’t always come with a response</a></strong><br />
          If your post meets all the guidelines and doesn’t need edits, we’ll go ahead and publish it without contacting you.
        </li>
        <li id="mod-3">
          <a className="rule-num" aria-label="Link to moderation rule 3" />
          <strong><a href="#mod-3" className="rule-link" aria-label="Link to moderation rule 3 title" onClick={handleHashClick}>Small edits vs. bigger changes</a></strong><br />
          We might fix small issues like typos or formatting. If your post needs more significant changes, we’ll ask you to revise.
        </li>
        <li id="mod-4">
          <a  className="rule-num" aria-label="Link to moderation rule 4" onClick={handleHashClick} />
          <strong><a href="#mod-4" className="rule-link" aria-label="Link to moderation rule 4 title" onClick={handleHashClick}>Comments can be sidenoted</a></strong><br />
          Both the Refresh Connections team and the author of a post can <a href="faqs#sidenote">sidenote comments</a>.
        </li>
        <li id="mod-5">
          <a className="rule-num" aria-label="Link to moderation rule 5" />
          <strong><a href="#mod-5" className="rule-link" aria-label="Link to moderation rule 5 title" onClick={handleHashClick}>Violations and enforcement</a></strong><br />
          We may temporarily or permanently remove posts or comments that violate these rules. Serious or repeated violations may result in account suspension or termination.
        </li>
        <li id="mod-6">
          <a className="rule-num" aria-label="Link to moderation rule 6" />
          <strong><a className="rule-link" aria-label="Link to moderation rule 6 title"><a href="/language">Language</a> and <a href="/science">STEAM guidelines</a> matter</a></strong><br />
          We strive to moderate for safety, clarity, and inclusion: especially in science, medicine, and health conversations, where bad patterns and sharp language can do real damage.
        </li>
      </ol>

      <p style={{ textAlign: "center", fontStyle: "italic", marginTop: "3rem" }}>
        Last updated June 2026
      </p>
    </div>
  );
};

export default RefreshmentsBarGuidelines;
